import model from "@js/model.js";
import { addDirPath, removeEmptyFromArr, isObject, firstIfUndefined, getParameterByName } from "@helpers/utils.js";
import { nextTick } from "vue";
import Enum from "@shared/enum.js";
import { oneStopShopJackpotPreprocess, setModel, topWinPreproccess, zitroJackpotPreprocess } from "../business/external-api-business.js";

var additionalConfig = {
	amusnetJackpot: {
		ignoreNetworkError: true,
	},
	oneStopShopJackpot: {
		preproccess: oneStopShopJackpotPreprocess,
		ignoreNetworkError: true,
	},
	zitroJackpot: {
		preproccess: zitroJackpotPreprocess,
		ignoreNetworkError: true,
	},
	topWin: {
		preproccess: topWinPreproccess,
		ignoreNetworkError: true,
	},
	ctinteractiveJackpot: {
		ignoreNetworkError: true,
	},
	jackpotWin: {
		preproccess(data){
			for(var i = 0, j = data.length; i < j; i++){
				var item = data[i];
				item.dateFormatted = model.formatDateTodayYesterday(item.Date);
				item.winNumber = Number(item.WinMon.replace(/[^0-9]/g, ''));
				item.Currency = item.Currency || 'EUR';
				var config = model.jackpotConfig && model.jackpotConfig.items;
				if(config) item.nameLabel = config[item.Level];
			}
		},
	},
	leaderboard: {
		getAuthUrl(label){
			var api = getAPI.call(this, label);
			if(typeof api.path !== 'string')
				return api.path;

			if(model.logged && model.userId)
				return addDirPath(api.path, model.userId);
			return api.path;
		},
		preproccess(data){
			conditionalTournamentsSetup(data);
			model.observables.leaderboardData = data;
		},
		underCondition(){
			return this.anyLocalTourFromPromoList();
		},
	},
	tournaments: {
		preproccess(data){
			conditionalTournamentsSetup(data);

			model.tourGames = {};
			model.tourGameGroupIds = {};
			model.tourIds = [];

			var arrProps = ['StartDate', 'EndDate', 'VisibleFrom', 'LeaderboardVisibleFrom', 'VisibleTo'];

			data.forEach(tour => {
				arrProps.forEach(key => tour[key] = (new Date(tour[key])).getTime());
				model.tourGameGroupIds[tour.TournamentId] = removeEmptyFromArr((tour.GameGroupIds || '').split(','));
				model.tourIds.push(tour.TournamentId);
			});

			this.whenGamesReady(() => {
				data.forEach(tour => {
					model.tourGames[tour.TournamentId] = this.excludeDeviceOpposedGameIds(removeEmptyFromArr((tour.GameIds || '').split(',')));
				});
			});
		},
		underCondition(){
			return this.anyLocalTourFromPromoList();
		},
	},
};

function conditionalTournamentsSetup(data){
	if(isObject(model.tournsUnderCondition)){
		for(let i = data.length - 1; i >= 0; i--){
			let item = data[i];
			let func = model.tournsUnderCondition[item.TournamentId];
			if(typeof func == 'function'){
				!func(model) && data.splice(data.indexOf(item), 1);
			}
			else if(typeof model.tournsUnderCondition.other === 'function'){
				!model.tournsUnderCondition.other(model) && data.splice(data.indexOf(item), 1);
			}
		}
	}
}

var timeouts = {};
var listObservers = {};

export default  {
	initExternalApis(){
		if(!model.isView(Enum.Views.MAIN)) return;

		setModel(model);

		model.bigWinThreshold = model.bigWinThreshold || 1000;
		if(!model.externalAPIs) return;

		/*
			Since external APIs take up a particular amount of resources, it's best 
			practice to avoid them on page load, thus why we're flagging all of them 
			as not activated. 
			*/
		Object.keys(model.externalAPIs).forEach(label => {
			var api = getAPI.call(this, label);
			if(!api) return;

			api.activated = false;
			api.shouldSendRequest = additionalConfig[label]?.shouldSendRequest;
		});
		
		var t;
		model.on('LoginComplete', 'LogoutComplete', 'SetLang', e => {
			clearTimeout(t);
			if(e.eventName === 'LogoutComplete' && e.isPageLoad) return;

			t = setTimeout(() => execAllActivatedAPIs.call(this), 2000);
		});
	},
	getExtAPI(){
		return getAPI.apply(this, arguments);
	},
	executeAPI(label, ignoreShouldSendReq, ignoreObserved, callback, global){
		var api = getAPI.call(this, label);
		if(!api) return;

		/*
			An API is flagged as active only when it's manually executed, particularly when 
			a web component actually has to display its data. 
			*/
		api.activated = true;
		fetchAPIData.call(this, label, ignoreShouldSendReq, ignoreObserved, callback, global);
	},
	unobserveElemIfAPIChange(label, element){
		var item = listObservers[label];
		if(!item) return;

		var ind = item.elements.indexOf(element);
		ind !== -1 && item.elements.splice(ind, 1);
		item.observer.unobserve(element);
	},
	observeElemIfAPIChange: (() => {
		function setupElem(label, element, delayInitialExecution){
			var item = listObservers[label];
			item.elements.push(element);
			item.observer.observe(element);

			if(delayInitialExecution) item.delayedResponses.push(element);

			nextTick(() => {
				let obsoleteElems = item.elements.filter(el => !el.parentNode);
				while(obsoleteElems.length > 0) this.unobserveElemIfAPIChange(label, obsoleteElems.pop());
			});
		};

		return function(label, element, delayInitialExecution){
			if(typeof delayInitialExecution !== 'boolean') delayInitialExecution = false;

			var api = getAPI.call(this, label);
			if(!api) return;
			
			if(listObservers[label]){
				setupElem.call(this, label, element, delayInitialExecution);
				return;
			}

			Object.defineProperty(api, 'observed', (() => {
				return {
					get: () => {
						var elems = listObservers[label].elements;
						return Boolean(elems.find(item => item.observed));
					},
				};
			})());

			var then = null;
			var observer = new IntersectionObserver(entries => {
				for(var i = 0, j = entries.length; i < j; i++){
					var entry = entries[i];
					var elem = entry.target;
					elem.observed = entry.isIntersecting;
					var now = Date.now();
					if(elem.observed && (then === null || (now - then >= 20 * 1000))){
						then = now;
						var delayedResps = listObservers[label].delayedResponses;
						if(delayedResps.includes(elem)){
							fetchAPIDataWithTimeout.call(this, label);
							delayedResps.splice(delayedResps.indexOf(elem), 1);
						}
						else
							this.executeAPI(label);
						break;
					}
				}
			});
			
			listObservers[label] = {
				elements: [],
				observer: observer,
				api: api,
				delayedResponses: [],
			};

			setupElem.call(this, label, element, delayInitialExecution);
		};
	})(),
	getTournGameIDs(id){
		var games = model.tourGames?.[id];
		if(games && games.length > 0) return games;

		games = [];
		var groupIDs = model.tourGameGroupIds && model.tourGameGroupIds[id];
		return this.getGamesByGroupIDs(groupIDs, true);
	},
	sendTopWinnersRequest(callback){
		(typeof model.urls.topWinners === 'string') && this.webServiceSend({
			url: model.urls.topWinners,
			success: data => {
				topWinPreproccess.call(this, data, true);
				model.topWinners = data;
				callback(data);
			},
			type: 'GET',
			global: false,
			dataType: 'json',
			contentType: false,
			processData: false,
		});
	}
}

function execAllActivatedAPIs(){
	Object.keys(model.externalAPIs).forEach(label => fetchAPIData.call(this, label));
}

function fetchAPIData(label, ignoreShouldSendReq, ignoreObserved, callback, global){
	clearTimeout(timeouts[label]);
	executeExternalAPI.call(this, label, ignoreShouldSendReq, ignoreObserved, data => {
		model.dispatchEvent('ListUpdated_' + label, {
			data: data,
		});
		typeof callback === 'function' && callback();
		fetchAPIDataWithTimeout.call(this, label, false, false, null, null, false);
	}, null, global, callback);
}

function fetchAPIDataWithTimeout(){
	if(arguments.length == 0) throw "No args specified!";
	var label = arguments[0];
	timeouts[label] = setTimeout(() => fetchAPIData.apply(this, arguments), getAPI.call(this, label).serverUpdateDelay || 10000);
}

function executeExternalAPI(label, ignoreShouldSendReq, ignoreObserved, onSuccess, onError, global, callbackIfCondFalse){
	if(typeof ignoreShouldSendReq !== 'boolean') ignoreShouldSendReq = false;
	if(typeof ignoreObserved !== 'boolean') ignoreObserved = false;
	if(typeof global !== 'boolean') global = false;

	var api = getAPI.call(this, label);
	if(!api) return;

	var conf = additionalConfig[label];
	if(isObject(conf) && typeof conf.underCondition === 'function' && !conf.underCondition.call(this)){
		typeof callbackIfCondFalse === 'function' && callbackIfCondFalse();
		return;
	}

	if(!api.activated || (!ignoreObserved && !api.observed) || (!ignoreShouldSendReq && typeof api.shouldSendRequest === 'function' ? !api.shouldSendRequest.call(this) : false)){
		fetchAPIDataWithTimeout.call(this, label);
		return;
	}

	var url = (isObject(conf) && typeof conf.getAuthUrl === 'function') ? conf.getAuthUrl.call(this, label) : api.path;

	/*
		If the path itself is an object, we're assuming 
		it has paths for different currencies. 
	*/
	if(!Array.isArray(url) && isObject(url)) url = firstIfUndefined(url, model.getCurrency().code);

	if(!url){
		typeof onError === 'function' && onError();
		return;
	}

	if(typeof url === 'string'){
		this.webServiceSend({
			url: url.replace('{currency}', model.getCurrency().code),
			success: data => onSuccessPre.call(this, data, label, onSuccess),
			error: onError,
			type: 'GET',
			global: global,
			dataType: 'json',
			contentType: false,
			processData: false,
			cache: (url.includes('.json') || url.includes('.xml')) ? false : undefined,
			ignoreNetworkError: conf?.ignoreNetworkError,
			onIgnoredNetworkError: () => fetchAPIDataWithTimeout.call(this, label),
		});
	}
	else{
		setTimeout(() => onSuccessPre.call(this, url, label, onSuccess, true), getParameterByName('longAPIRespDelay') == 'true' ? 1000000 : 50);
		console.log('%c' + label + ': TEST DATA UPDATED', 'padding:5px; background-color:#000; color: #0f0;');
	}
}

function onSuccessPre(data, label, callback){
	if(!data || (Array.isArray(data) ? (data.length ? data[0].Error : false) : false)){
		fetchAPIDataWithTimeout.call(this, label);
		return;
	}
	
	if(isObject(window._testNewExtAPIData)){
		var extraTestData = window._testNewExtAPIData[label];
		if(Array.isArray(extraTestData)){
			data = data.concat(extraTestData);
			getAPI.call(this, label).path = data;
			delete window._testNewExtAPIData[label];
		}
	}

	var _case = additionalConfig[label];
	if(_case && typeof _case.preproccess === 'function') _case.preproccess.call(this, data);

	getAPI.call(this, label).data = data;		
	if(typeof callback === 'function') callback(data);
}

function getAPI(label){
	if(isObject(model.externalAPIs)) return model.externalAPIs[label];
}